const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export const DEMO_ROLE_IDS = new Set([3, 4]);

export const emptyEventForm = {
  nom_event: "",
  description_event: "",
  lieu_event: "",
  date_event: "",
  photoURL: "",
  is_active: true,
};

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function isDemoMode() {
  const user = getStoredUser();
  return DEMO_ROLE_IDS.has(Number(user?.role_id));
}

export function getFallbackSectionId(sectionId) {
  if (sectionId) return Number(sectionId);

  const user = getStoredUser();

  return user?.section_id ? Number(user.section_id) : null;
}

export function isExternalURL(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function resolveMediaUrl(url, fallback = "/images/event_placeholder.jpg") {
  if (!url) return fallback;

  const value = String(url).trim();

  if (!value) return fallback;

  if (
    isExternalURL(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

export function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function stripHtml(value = "") {
  return String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function truncateText(value = "", max = 120) {
  const clean = stripHtml(value);

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max).trim()}...`;
}

export function toDateInputValue(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return date.toISOString().slice(0, 10);
}

export function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function isUpcomingEvent(event) {
  if (!event?.date_event) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = new Date(event.date_event);
  eventDate.setHours(0, 0, 0, 0);

  return eventDate >= today;
}

export function mapEventToForm(event) {
  return {
    nom_event: event?.nom_event || "",
    description_event: event?.description_event || "",
    lieu_event: event?.lieu_event || "",
    date_event: toDateInputValue(event?.date_event),
    photoURL: event?.photoURL || "",
    is_active:
      typeof event?.is_active === "boolean" ? event.is_active : true,
  };
}

export function buildEventPayload(formData, sectionId) {
  return {
    nom_event: formData.nom_event,
    description_event: formData.description_event,
    lieu_event: formData.lieu_event,
    date_event: formData.date_event,
    photoURL: formData.photoURL,
    is_active: Boolean(formData.is_active),
    section_id: Number(sectionId),
  };
}

export function sortEvents(events = []) {
  return [...events].sort((a, b) => {
    const da = new Date(a?.date_event || 0).getTime();
    const db = new Date(b?.date_event || 0).getTime();

    const aUpcoming = isUpcomingEvent(a);
    const bUpcoming = isUpcomingEvent(b);

    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;

    return da - db;
  });
}

export function filterEvents(
  events = [],
  searchTerm = "",
  statusFilter = "all",
  periodFilter = "all"
) {
  const q = normalizeText(searchTerm.trim());

  return events.filter((event) => {
    const active = event?.is_active !== false;
    const upcoming = isUpcomingEvent(event);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && active) ||
      (statusFilter === "inactive" && !active);

    const matchesPeriod =
      periodFilter === "all" ||
      (periodFilter === "upcoming" && upcoming) ||
      (periodFilter === "past" && !upcoming);

    const searchable = normalizeText(
      [
        event?.nom_event,
        event?.description_event,
        event?.lieu_event,
        event?.section?.nom_section,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchesSearch = !q || searchable.includes(q);

    return matchesStatus && matchesPeriod && matchesSearch;
  });
}

export function getAgendaStats(events = []) {
  const total = events.length;
  const active = events.filter((event) => event?.is_active !== false).length;
  const inactive = total - active;
  const upcoming = events.filter(isUpcomingEvent).length;
  const past = total - upcoming;

  return {
    total,
    active,
    inactive,
    upcoming,
    past,
  };
}